import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { dispatchAPI, contractsAPI } from '@/lib/api';

// Helper to download a blob as a file
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function DispatchPlanReportPage() {
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState('plan');

  useEffect(() => {
    (async () => {
      try {
        const res = await contractsAPI.activeCustomers();
        setCustomers(res.data?.customers || res.data || []);
      } catch {
        toast.error('Failed to load customers');
      }
    })();
  }, []);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-blue-600" /> Customer Dispatch Reports
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Export dispatch plan matrices and raw-material stock for selected items, by customer.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="dispatch-report-tabs">
        <TabsList>
          <TabsTrigger value="plan" data-testid="tab-plan">Dispatch Plan</TabsTrigger>
          <TabsTrigger value="rmstock" data-testid="tab-rmstock">RM / Job Work Stock</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Dispatch History</TabsTrigger>
        </TabsList>

        <TabsContent value="plan">
          <DispatchPlan customers={customers} />
        </TabsContent>
        <TabsContent value="rmstock">
          <RmJwStock customers={customers} />
        </TabsContent>
        <TabsContent value="history">
          <DispatchHistory customers={customers} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ====================================================================
// Sub-component: Dispatch Plan matrix
// ====================================================================

function DispatchPlan({ customers }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState(fmt(monthStart));
  const [dateTo, setDateTo] = useState(fmt(today));
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadItems = async () => {
    if (!customerId) {
      toast.error('Select a customer first');
      return;
    }
    setLoading(true);
    setSelected([]);
    try {
      const res = await dispatchAPI.getCustomerDispatchedItems({
        customer_id: customerId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dispatched items');
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items.map(i => i.item_id));
  };

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exportXlsx = async () => {
    if (selected.length === 0) { toast.error('Select at least one item'); return; }
    setExporting(true);
    try {
      const res = await dispatchAPI.exportDispatchPlan({
        customer_id: customerId,
        date_from: dateFrom,
        date_to: dateTo,
        item_ids: selected,
      });
      const cn = (customers.find(c => c.id === customerId)?.customer_name || 'Customer').replace(/\W+/g, '_');
      downloadBlob(res.data, `Dispatch_Plan_${cn}_${dateFrom}_to_${dateTo}.xlsx`);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Dispatch Plan (matrix)</CardTitle>
        <p className="text-xs text-muted-foreground">
          Cross-tab of date × selected parts, showing the actual dispatched quantity per day for the selected period. Days with no dispatch are shown as <strong>0</strong>.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="plan-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date From *</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs mt-1" data-testid="plan-date-from" />
          </div>
          <div>
            <Label className="text-xs">Date To *</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs mt-1" data-testid="plan-date-to" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadItems} disabled={loading || !customerId} className="h-9" data-testid="plan-load-items">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Load Items
            </Button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{items.length} items dispatched in this period • {selected.length} selected</p>
              <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs" data-testid="plan-select-all">
                {selected.length === items.length ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="border rounded-md max-h-[320px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Item Name</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-center">UOM</th>
                    <th className="p-2 text-right">Total Qty</th>
                    <th className="p-2 text-center">DCs</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(it => (
                    <tr key={it.item_id} onClick={() => toggle(it.item_id)} className="hover:bg-muted/30 cursor-pointer" data-testid={`plan-item-${it.item_unique_id}`}>
                      <td className="p-2"><Checkbox checked={selected.includes(it.item_id)} onCheckedChange={() => toggle(it.item_id)} /></td>
                      <td className="p-2">{it.item_name}</td>
                      <td className="p-2 font-mono">{it.item_unique_id}</td>
                      <td className="p-2 text-center text-muted-foreground">{it.uom}</td>
                      <td className="p-2 text-right font-mono">{it.total_qty}</td>
                      <td className="p-2 text-center text-muted-foreground">{it.dc_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={exportXlsx} disabled={exporting || selected.length === 0} data-testid="plan-export-btn">
              <Download className="w-3.5 h-3.5 mr-1" />
              {exporting ? 'Generating…' : 'Generate & Download XLSX'}
            </Button>
          </div>
        )}
        {items.length === 0 && customerId && !loading && (
          <p className="text-xs text-muted-foreground italic">No items dispatched to this customer in the selected period — try a wider date range.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ====================================================================
// Sub-component: Raw Material / Job Work stock (direct selection)
// ====================================================================

function RmJwStock({ customers }) {
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [linkedToPrograms, setLinkedToPrograms] = useState(false);

  const loadItems = async () => {
    if (!customerId) { toast.error('Select a customer first'); return; }
    setLoading(true);
    setSelected([]);
    try {
      const res = await dispatchAPI.getCustomerRmJwItems({ customer_id: customerId });
      setItems(res.data?.items || []);
      setLinkedToPrograms(!!res.data?.linked_to_programs);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === items.length ? [] : items.map(i => i.item_id));

  const exportXlsx = async () => {
    if (selected.length === 0) { toast.error('Select at least one item'); return; }
    setExporting(true);
    try {
      const res = await dispatchAPI.exportRmJwStock({
        customer_id: customerId,
        item_ids: selected,
      });
      const cn = (customers.find(c => c.id === customerId)?.customer_name || 'Customer').replace(/\W+/g, '_');
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(res.data, `RM_JW_Stock_${cn}_${today}.xlsx`);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Raw Material / Job Work Item Stock</CardTitle>
        <p className="text-xs text-muted-foreground">
          Live stock for selected <strong>Raw Material</strong> (received from customer) and <strong>Job Work Item</strong> entries. The export shows <strong>OK Stock</strong> (available — same formula as Item Master, excluding QC/Quarantine/Inbound-Staging/Rejection), plus separate <strong>Quarantined</strong> and <strong>Pending Inspection</strong> (Inbound Staging + QC) columns.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="rm-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <Button onClick={loadItems} disabled={loading || !customerId} className="h-9" data-testid="rm-load-items">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Load Items
            </Button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {items.length} item(s) {linkedToPrograms ? 'linked to this customer\'s programs' : '(no program link — showing all RM + Job Work items)'} • {selected.length} selected
              </p>
              <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs" data-testid="rm-select-all">
                {selected.length === items.length ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="border rounded-md max-h-[360px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Item Name</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-center">UOM</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(it => (
                    <tr key={it.item_id} onClick={() => toggle(it.item_id)} className="hover:bg-muted/30 cursor-pointer" data-testid={`rm-item-${it.item_unique_id}`}>
                      <td className="p-2"><Checkbox checked={selected.includes(it.item_id)} onCheckedChange={() => toggle(it.item_id)} /></td>
                      <td className="p-2">{it.item_name}</td>
                      <td className="p-2 font-mono">{it.item_unique_id}</td>
                      <td className="p-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${it.item_type === 'Raw Material' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-purple-50 text-purple-700 border-purple-300'}`}>
                          {it.item_type}
                        </span>
                      </td>
                      <td className="p-2 text-center text-muted-foreground">{it.uom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={exportXlsx} disabled={exporting || selected.length === 0} data-testid="rm-export-btn">
              <Download className="w-3.5 h-3.5 mr-1" />
              {exporting ? 'Generating…' : 'Generate & Download XLSX'}
            </Button>
          </div>
        )}
        {items.length === 0 && customerId && !loading && (
          <p className="text-xs text-muted-foreground italic">No Raw Material / Job Work items found. Confirm such items exist in Item Master.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ====================================================================
// Sub-component: Dispatch History (flat per-line export, filter by customer + parts + range)
// ====================================================================

function DispatchHistory({ customers }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState(fmt(monthStart));
  const [dateTo, setDateTo] = useState(fmt(today));
  const [dateBasis, setDateBasis] = useState('dc'); // 'dc' | 'invoice'
  const [onlyReconciled, setOnlyReconciled] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadItems = async () => {
    if (!customerId) { toast.error('Select a customer first'); return; }
    setLoading(true);
    setSelected([]);
    try {
      const res = await dispatchAPI.getCustomerDispatchedItems({
        customer_id: customerId, date_from: dateFrom, date_to: dateTo,
      });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dispatched items');
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items.map(i => i.item_id));
  };
  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await dispatchAPI.exportDispatchHistory({
        customer_id: customerId,
        date_from: dateFrom,
        date_to: dateTo,
        // empty array = ALL parts dispatched to this customer in range
        item_ids: selected,
        date_basis: dateBasis,
        only_reconciled: onlyReconciled,
      });
      const cn = (customers.find(c => c.id === customerId)?.customer_name || 'Customer').replace(/\W+/g, '_');
      downloadBlob(res.data, `Dispatch_History_${cn}_${dateFrom}_to_${dateTo}.xlsx`);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Dispatch History (flat list)</CardTitle>
        <p className="text-xs text-muted-foreground">
          Per-DC-line export with Dispatch Date, Part Number, and the Invoice # (when reconciled) or DC # (otherwise). Leave Part Number empty to include all parts dispatched to this customer in the date range.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="history-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date Basis</Label>
            <Select value={dateBasis} onValueChange={setDateBasis}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="history-date-basis">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dc">DC Date</SelectItem>
                <SelectItem value="invoice">Invoice Date (reconciled only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date From *</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs mt-1" data-testid="history-date-from" />
          </div>
          <div>
            <Label className="text-xs">Date To *</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs mt-1" data-testid="history-date-to" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadItems} disabled={loading || !customerId} className="h-9" data-testid="history-load-items">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Load Items
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 pl-1">
          <Checkbox id="history-only-rec" checked={onlyReconciled} onCheckedChange={v => setOnlyReconciled(!!v)} data-testid="history-only-reconciled" />
          <Label htmlFor="history-only-rec" className="text-xs cursor-pointer">
            Only reconciled dispatches (DCs with a customer invoice number)
          </Label>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {items.length} parts dispatched in this period • {selected.length} selected (empty = all)
              </p>
              <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs" data-testid="history-select-all">
                {selected.length === items.length ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="border rounded-md max-h-[320px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Item Name</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-center">UOM</th>
                    <th className="p-2 text-right">Total Qty</th>
                    <th className="p-2 text-center">DCs</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(it => (
                    <tr key={it.item_id} onClick={() => toggle(it.item_id)} className="hover:bg-muted/30 cursor-pointer" data-testid={`history-item-${it.item_unique_id}`}>
                      <td className="p-2"><Checkbox checked={selected.includes(it.item_id)} onCheckedChange={() => toggle(it.item_id)} /></td>
                      <td className="p-2">{it.item_name}</td>
                      <td className="p-2 font-mono">{it.item_unique_id}</td>
                      <td className="p-2 text-center">{it.uom}</td>
                      <td className="p-2 text-right">{it.total_qty}</td>
                      <td className="p-2 text-center">{it.dc_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button onClick={exportXlsx} disabled={exporting || !customerId} className="h-9" data-testid="history-export-btn">
            <Download className={`w-3.5 h-3.5 mr-1 ${exporting ? 'animate-spin' : ''}`} />
            Download Excel
          </Button>
        </div>
        {!customerId && (
          <p className="text-xs text-muted-foreground italic">Select a customer and date range, then click Download Excel. Item selection is optional.</p>
        )}
      </CardContent>
    </Card>
  );
}
